import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';

/**
 * Composant EmptyState réutilisable pour toute l'application.
 * @param {ReactNode} icon - Icône Lucide (ex: <FileText />)
 * @param {string} title - Titre principal
 * @param {string} description - Description courte
 * @param {string} actionLabel - Texte du bouton CTA (optionnel)
 * @param {function} onAction - Callback du bouton CTA (optionnel)
 * @param {string} actionHref - Lien du bouton CTA (optionnel, alternatif à onAction)
 * @param {string} testId - data-testid
 * @param {boolean} compact - Mode compact (moins de padding)
 */
export default function EmptyState({
    icon,
    title,
    description,
    actionLabel,
    onAction,
    actionHref,
    testId = 'empty-state',
    compact = false
}) {
    const Wrapper = actionHref ? 'a' : 'div';

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex flex-col items-center justify-center text-center ${compact ? 'py-6 px-4' : 'py-10 sm:py-16 px-6'}`}
            data-testid={testId}
        >
            {icon && (
                <div className="w-12 h-12 rounded-2xl bg-[#681793]/10 border border-[#681793]/20 flex items-center justify-center mb-4 text-[#FF914D]">
                    {React.cloneElement(icon, { className: 'w-5 h-5' })}
                </div>
            )}
            <h3 className={`font-semibold text-foreground ${compact ? 'text-sm mb-1' : 'text-base sm:text-lg mb-1.5'}`}>
                {title}
            </h3>
            <p className={`text-muted-foreground max-w-sm ${compact ? 'text-xs' : 'text-xs sm:text-sm'}`}>
                {description}
            </p>
            {actionLabel && (onAction || actionHref) && (
                <Button
                    size={compact ? 'sm' : 'default'}
                    className="mt-4 bg-[#FF914D] hover:bg-[#FF914D]/90 text-white"
                    onClick={onAction}
                    {...(actionHref ? { asChild: true } : {})}
                    data-testid={`${testId}-action`}
                >
                    {actionHref ? <a href={actionHref}>{actionLabel}</a> : actionLabel}
                </Button>
            )}
        </motion.div>
    );
}
